import React, { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormControl, FormHelperText } from '@mui/material';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import SelectWithSearch from 'src/components/SelectWithSearch/view';
import { formatGroupContactCountSuffix } from 'src/components/GroupOptionLabel';
import { AddContactsToGroupDialog } from 'src/modules/contacts/components';
import { useAddContactsToGroupDialogController } from 'src/modules/contacts/controllers';
import { DynamicGroupRulesFormDialog, GroupFormDialog } from 'src/modules/groups/components';
import {
  useDynamicGroupRulesFormDialogController,
  useGroupFormDialogController,
} from 'src/modules/groups/controllers';
import { useGroupsApi } from 'src/modules/groups/api';
import { Group } from 'src/modules/groups/types';
import { useCampaignBuilderV2Api } from '../../api';
import {
  useBuilderConfig,
  useBuilderContext,
  useCampaignFormContext,
  useField,
  useIsReadOnly,
} from '../../context';
import { resolvePolicy } from '../../policies';

type SelectOption = { text: string; secondaryText?: string; value: string; isArchived?: boolean };

export const RecipientsField: React.FC = () => {
  const { value: groupIds, error, onChange } = useField('groupIds');
  const { getValues } = useCampaignFormContext();
  const companyId = useSelector(getCurrentCompanyId) as string;
  const config = useBuilderConfig();
  const { campaignGroups = [] } = useBuilderContext();
  const api = useCampaignBuilderV2Api();
  const { getGroup: getGroupApi } = useGroupsApi();
  const disabled = useIsReadOnly();
  const [groups, setGroups] = useState<Group[]>(campaignGroups);

  const groupFormDialogController = useGroupFormDialogController();
  const dynamicGroupRulesFormDialogController = useDynamicGroupRulesFormDialogController();
  const addContactsToGroupDialogController = useAddContactsToGroupDialogController();

  // A group is created in one dialog and then optionally refined in a second
  // (rules for dynamic groups, contacts for standard ones). It is only selected
  // once that follow-up finishes or is skipped — hence the pending handoff.
  const pendingGroupsRef = useRef<Record<'dynamic' | 'standard', Group | null>>({
    dynamic: null,
    standard: null,
  });

  // Every audience rule for this campaign type comes from its policy.
  const { label, groupTypes, allowDynamicGroups, initialGroupType } =
    resolvePolicy(config).recipients(config);

  useEffect(() => {
    let alive = true;
    if (!companyId) return;
    // Archived groups included so a campaign already targeting one still shows
    // its name.
    api.getGroups({
      companyId,
      limit: 1000,
      offset: 0,
      statuses: 'active,archived',
      ...(groupTypes ? { types: groupTypes } : {}),
    })
      // Merge API results with groups already known from the campaign rather than
      // replacing them, so a freshly created group is not dropped by a
      // late-arriving response.
      .then((res) => {
        if (!alive) return;
        const items = res.data.items ?? [];
        setGroups(prev => {
          const apiIds = items.map(g => g?.id).filter(Boolean);
          return [...prev.filter(g => g?.id && !apiIds.includes(g.id)), ...items.filter(Boolean)];
        });
      });
    return () => { alive = false; };
  }, [companyId, api, groupTypes]);

  const selectCreatedGroup = useCallback((newGroup: Group) => {
    setGroups(prev => [...prev.filter(group => group.id !== newGroup.id), newGroup]);
    const current = getValues().groupIds ?? [];
    onChange([...new Set([...current, newGroup.id])]);
  }, [getValues, onChange]);

  const commitPendingGroup = useCallback((type: 'dynamic' | 'standard') => {
    const pendingGroup = pendingGroupsRef.current[type];
    if (!pendingGroup) return;
    pendingGroupsRef.current[type] = null;
    selectCreatedGroup(pendingGroup);
  }, [selectCreatedGroup]);

  const handleGroupCreated = useCallback(async (newGroup?: Group) => {
    if (!newGroup) return;

    if (newGroup.type === 'dynamic') {
      pendingGroupsRef.current.dynamic = newGroup;
      const didOpen = await dynamicGroupRulesFormDialogController.open(newGroup);
      if (!didOpen) commitPendingGroup('dynamic');
    } else {
      pendingGroupsRef.current.standard = newGroup;
      const didOpen = await addContactsToGroupDialogController.open(newGroup.id, newGroup.name);
      if (!didOpen) commitPendingGroup('standard');
    }
  }, [
    addContactsToGroupDialogController.open,
    commitPendingGroup,
    dynamicGroupRulesFormDialogController.open,
  ]);

  const handleAddGroup = (groupName: string) => {
    groupFormDialogController.open(undefined, {
      initialName: groupName,
      initialType: initialGroupType,
    });
  };

  // After contacts are added, re-read the group so its contact count is current before it lands
  // in the picker.
  const refreshPendingStandardGroup = useCallback(async () => {
    const pendingGroup = pendingGroupsRef.current.standard;
    if (!pendingGroup) return;

    if (!companyId) {
      commitPendingGroup('standard');
      return;
    }

    try {
      const response = await getGroupApi(companyId, pendingGroup.id);
      pendingGroupsRef.current.standard = null;
      selectCreatedGroup(response.data);
    } catch (e) {
      console.error('Failed to refresh created group', e);
      commitPendingGroup('standard');
    }
  }, [commitPendingGroup, getGroupApi, companyId, selectCreatedGroup]);

  const options: SelectOption[] = useMemo(() => {
    const toOption = ({ name, id, status, validContactCount }: Group): SelectOption => ({
      text: name || '',
      secondaryText: formatGroupContactCountSuffix(validContactCount),
      value: id || '',
      isArchived: status === 'archived',
    });
    return [
      ...groups.filter(g => g && g.type === 'default').map(toOption),
      ...groups
        .filter(g => g && g.type !== 'default')
        .sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()))
        .map(toOption),
    ];
  }, [groups]);

  return (
    <>
      <FormControl sx={{ width: '100%', mt: 1.25, mb: 2.5 }} error={!!error}>
        <SelectWithSearch
          disabled={disabled}
          label={label}
          placeholder={label}
          options={options}
          values={groupIds ?? []}
          handleSelectChange={(_: SyntheticEvent | undefined, selected: SelectOption[]) =>
            onChange(selected.map((s) => s.value))
          }
          handleAddItem={handleAddGroup}
          entity="Group"
          style={{ width: '100%', maxHeight: '120px' }}
        />
        <FormHelperText>{error ?? ''}</FormHelperText>
      </FormControl>
      <GroupFormDialog
        {...groupFormDialogController}
        allowDynamicType={allowDynamicGroups}
        submit={(full, dirty) => groupFormDialogController.submit(full, dirty, handleGroupCreated)}
      />
      <DynamicGroupRulesFormDialog
        {...dynamicGroupRulesFormDialogController}
        close={() => {
          dynamicGroupRulesFormDialogController.close();
          commitPendingGroup('dynamic');
        }}
        submit={async (full, dirty, nextAction) => {
          const updatedGroup = await dynamicGroupRulesFormDialogController.submit(
            full,
            dirty,
            nextAction,
          );
          if (updatedGroup) {
            pendingGroupsRef.current.dynamic = null;
            selectCreatedGroup(updatedGroup);
          }
          return updatedGroup;
        }}
      />
      <AddContactsToGroupDialog
        {...addContactsToGroupDialogController}
        close={() => {
          addContactsToGroupDialogController.close();
          commitPendingGroup('standard');
        }}
        onSuccess={refreshPendingStandardGroup}
      />
    </>
  );
};
